interface CookiebotApi {
    consent?: { marketing?: boolean };
    show(): void;
}

declare global {
    interface Window {
        Cookiebot?: CookiebotApi;
    }
}

/**
 * Click-to-play gate for consent-blocked Vimeo iframes.
 *
 * The placeholder (poster image + play button) is always shown until the
 * user presses play; the player iframe never loads on its own. On click:
 * - marketing consent given, or no Cookiebot on this environment at all
 *   (local/dev, China mirror) -> load the player.
 * - Cookiebot present without marketing consent -> open the Cookiebot
 *   dialog; the pending play intent is honored as soon as the user accepts.
 * Declining while playing unloads the player and brings the gate back.
 */
export class VimeoConsent {
    private readonly cookieblockSrcByIframe = new Map<
        HTMLIFrameElement,
        string
    >();

    private readonly playIntent = new Set<HTMLIFrameElement>();

    constructor() {
        const iframes = Array.from(
            document.querySelectorAll<HTMLIFrameElement>(
                "iframe[data-cookieblock-src][data-cookieconsent='marketing']"
            )
        );
        if (!iframes.length) {
            return;
        }

        // Cache each iframe's blocked src once, up front: Cookiebot's uc.js
        // swaps/removes data-cookieblock-src itself once consent is granted,
        // so reading it lazily in activate() can race with that swap and
        // find the attribute already gone.
        iframes.forEach((iframe) => {
            const src = iframe.dataset.cookieblockSrc;
            if (src) {
                this.cookieblockSrcByIframe.set(iframe, src);
            }
        });

        iframes.forEach((iframe) => this.wirePlayButton(iframe));

        const sync = () => this.sync(iframes);

        window.addEventListener("CookiebotOnConsentReady", sync);
        window.addEventListener("CookiebotOnAccept", sync);
        window.addEventListener("CookiebotOnDecline", sync);

        if (window.Cookiebot) {
            sync();
        }
    }

    private static marketingConsent(): boolean {
        const cookiebot = window.Cookiebot;
        return Boolean(
            cookiebot && cookiebot.consent && cookiebot.consent.marketing
        );
    }

    private sync(iframes: HTMLIFrameElement[]): void {
        const marketing = VimeoConsent.marketingConsent();

        iframes.forEach((iframe) => {
            if (marketing) {
                this.consentTextFor(iframe, true);
                // Only start playback the user actually asked for. Cookiebot's
                // own uc.js may still swap the src by itself; that is fine.
                if (this.playIntent.has(iframe)) {
                    this.activate(iframe);
                }
            } else {
                // Declined (or consent withdrawn): unload the player if it
                // was running and bring the gate back.
                if (iframe.src && iframe.src !== "about:blank") {
                    iframe.src = "about:blank";
                }
                this.playIntent.delete(iframe);
                const placeholder = this.placeholderFor(iframe);
                if (placeholder) {
                    placeholder.hidden = false;
                }
                this.consentTextFor(iframe, false);
            }
        });
    }

    private activate(iframe: HTMLIFrameElement): void {
        const src = this.cookieblockSrcByIframe.get(iframe);
        if (src && iframe.src !== src) {
            iframe.src = src;
        }
        this.playIntent.delete(iframe);
        const placeholder = this.placeholderFor(iframe);
        if (placeholder) {
            placeholder.hidden = true;
        }
    }

    private placeholderFor(iframe: HTMLIFrameElement): HTMLElement | null {
        const parent = iframe.parentElement;
        return parent
            ? parent.querySelector<HTMLElement>(
                  ".verderliquids-keyvisual-consent-placeholder"
              )
            : null;
    }

    private consentTextFor(iframe: HTMLIFrameElement, hide: boolean): void {
        const placeholder = this.placeholderFor(iframe);
        const text = placeholder
            ? placeholder.querySelector<HTMLElement>(
                  ".verderliquids-keyvisual-consent-text"
              )
            : null;
        if (text) {
            text.hidden = hide;
        }
    }

    private wirePlayButton(iframe: HTMLIFrameElement): void {
        const placeholder = this.placeholderFor(iframe);
        const button = placeholder
            ? placeholder.querySelector<HTMLButtonElement>("button")
            : null;
        if (!button) {
            return;
        }

        button.addEventListener("click", () => {
            if (!window.Cookiebot || VimeoConsent.marketingConsent()) {
                // Consent given, or no consent stack on this environment:
                // the user's click is the go-ahead.
                this.activate(iframe);
                return;
            }

            // Cookiebot present, marketing declined/undecided: remember the
            // intent and let the user decide in the Cookiebot dialog; sync()
            // starts playback if they accept.
            this.playIntent.add(iframe);
            window.Cookiebot.show();
        });
    }
}
